<template>
  <el-dialog
    v-model="visible"
    width="700px"
    align-center
    append-to-body
    :show-close="false"
    modal-class="enterprise-advanced-settings-overlay"
    class="enterprise-advanced-settings-dialog"
  >
    <div class="enterprise-advanced-settings" v-loading="loading">
      <header class="enterprise-advanced-settings__header">
        <h2>{{ t('enterprise_ui_advanced_title') }}</h2>
        <button type="button" :aria-label="t('enterprise_ui_advanced_close')" @click="close">
          <i class="iconfont icon-icon_close" />
        </button>
      </header>

      <div class="enterprise-advanced-settings__grid">
        <button
          v-for="item in sections"
          :key="item.key"
          type="button"
          class="enterprise-advanced-settings__card"
          :class="{ 'is-active': isSectionActive(item.key), 'is-disabled': isSectionDisabled(item.key) }"
          :disabled="isSectionDisabled(item.key)"
          :aria-pressed="isSectionActive(item.key)"
          @click="toggleSection(item)"
        >
          <span class="enterprise-advanced-settings__card-title">{{ item.title }}</span>
          <span class="enterprise-advanced-settings__card-desc">{{ item.description }}</span>
        </button>
        <span class="enterprise-advanced-settings__placeholder" aria-hidden="true" />
      </div>

      <div v-if="activeSections.length" class="enterprise-advanced-settings__options">
        <section v-if="isSectionActive('ai') && optionGroups.ai.length">
          <h3>{{ t('enterprise_ui_advanced_polishing_strength') }}</h3>
          <div class="enterprise-advanced-settings__pills">
            <button
              v-for="option in optionGroups.ai"
              :key="option.id"
              type="button"
              :class="{ 'is-active': isOptionActive('ai', option.id) }"
              @click="selectSingleOption('ai', option.id)"
            >
              {{ getOptionLabel(option) }}
            </button>
          </div>
        </section>

        <section v-if="isSectionActive('style')">
          <h3>{{ t('enterprise_ui_advanced_choose_style') }}</h3>
          <div class="enterprise-advanced-settings__pills">
            <button
              v-for="option in optionGroups.style"
              :key="option.id"
              type="button"
              :class="{ 'is-active': isOptionActive('style', option.id) && !customStyleActive }"
              @click="selectStyleOption(option.id)"
            >
              {{ getOptionLabel(option) }}
            </button>
            <button type="button" :class="{ 'is-active': customStyleActive }" @click="selectCustomStyle">
              {{ t('enterprise_ui_advanced_custom') }}
            </button>
          </div>
          <el-input
            v-if="customStyleActive"
            v-model="draft.customizedStyle"
            class="enterprise-advanced-settings__textarea"
            type="textarea"
            resize="none"
            :rows="3"
            maxlength="200"
            :placeholder="t('enterprise_ui_advanced_custom_style_placeholder')"
          />
          <p v-if="customStyleActive" class="enterprise-advanced-settings__counter">
            {{ t('enterprise_ui_advanced_remaining_characters', { count: Math.max(200 - draft.customizedStyle.length, 0) }) }}
          </p>
        </section>

        <section v-if="isSectionActive('industry')">
          <h3>{{ t('enterprise_ui_advanced_choose_industry') }}</h3>
          <div class="enterprise-advanced-settings__pills">
            <button
              v-for="option in optionGroups.industry"
              :key="option.id"
              type="button"
              :class="{ 'is-active': isOptionActive('industry', option.id) }"
              @click="selectSingleOption('industry', option.id)"
            >
              {{ getOptionLabel(option) }}
            </button>
            <button type="button" :class="{ 'is-active': otherIndustryActive }" @click="selectOtherIndustry">
              {{ t('enterprise_ui_advanced_other_industry') }} +
            </button>
          </div>
          <div v-if="otherIndustryActive" class="enterprise-advanced-settings__inline-form">
            <el-input
              v-model="customIndustry"
              maxlength="50"
              :placeholder="t('enterprise_ui_advanced_industry_placeholder')"
              @keyup.enter="submitCustomIndustry"
            />
            <button type="button" @click="submitCustomIndustry">{{ t('enterprise_ui_advanced_submit') }}</button>
          </div>
          <p>{{ t('enterprise_ui_advanced_industry_tip') }}</p>
        </section>

        <section v-if="isSectionActive('language') && optionGroups.language.length">
          <h3>{{ t('enterprise_ui_advanced_keep_language') }}</h3>
          <div class="enterprise-advanced-settings__pills">
            <button
              v-for="option in optionGroups.language"
              :key="option.id"
              type="button"
              :class="{ 'is-active': isOptionActive('language', option.id) }"
              @click="selectSingleOption('language', option.id)"
            >
              {{ getOptionLabel(option) }}
            </button>
          </div>
        </section>

        <section v-if="isSectionActive('glossary')">
          <h3>{{ t('enterprise_ui_advanced_choose_glossary') }}</h3>
          <div class="enterprise-advanced-settings__term-row">
            <div class="enterprise-advanced-settings__term-select">
              <TermListComponents
                :key="termPickerKey"
                width="min(536px, calc(100vw - 64px))"
                :active-ids="termPickerActiveIds"
                @on-updata-art="updateTerms"
                @on-close="close"
              />
            </div>
            <NuxtLink :to="localePath('/account?menu=glossary')" @click="close">
              {{ t('enterprise_ui_advanced_manage_glossary') }}
            </NuxtLink>
          </div>
          <p>{{ t('enterprise_ui_advanced_glossary_tip') }}</p>
        </section>
      </div>

      <footer class="enterprise-advanced-settings__footer">
        <button type="button" class="is-secondary" @click="close">{{ t('cancel') }}</button>
        <button type="button" class="is-primary" @click="confirm">{{ t('enterprise_ui_advanced_confirm') }}</button>
      </footer>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import HttpRequest from '@/services/request'
import { PromptApi } from '@/services/api'
import TermListComponents from '@/components/TranslateComponents/TermListComponents/TermListItem.vue'
import Toast from '@/utils/toast'

type SectionKey = 'ai' | 'style' | 'industry' | 'language' | 'glossary'

interface PromptOption {
  id: number
  parentId?: number | null
  promptWord?: string
  wordsKey?: string
  children?: PromptOption[]
}

export interface EnterpriseAdvancedSettingsValue {
  activeSections: SectionKey[]
  optionsActive: Partial<Record<SectionKey, number[]>>
  promptWordIds: string
  customizedStyle: string
  termIds: string
}

const props = withDefaults(defineProps<{
  modelValue?: Partial<EnterpriseAdvancedSettingsValue>
  disableKeepLanguage?: boolean
}>(), {
  modelValue: () => ({}),
  disableKeepLanguage: false,
})

const emit = defineEmits<{
  confirm: [value: EnterpriseAdvancedSettingsValue]
}>()

const localePath = useLocalePath()
const { t, te } = useI18n()
const visible = ref(false)
const loading = ref(false)
const promptConfig = ref<PromptOption[]>([])
const termPickerKey = ref(0)
const termPickerActiveIds = ref('')
const customStyleActive = ref(false)
const otherIndustryActive = ref(false)
const customIndustry = ref('')

const sections = computed<Array<{ key: SectionKey; id: number; title: string; description: string }>>(() => [
  { key: 'ai', id: 1, title: t('enterprise_ui_advanced_ai_title'), description: t('enterprise_ui_advanced_ai_description') },
  { key: 'style', id: 2, title: t('enterprise_ui_advanced_style_title'), description: t('enterprise_ui_advanced_style_description') },
  { key: 'industry', id: 10, title: t('enterprise_ui_advanced_industry_title'), description: t('enterprise_ui_advanced_industry_description') },
  { key: 'language', id: 11, title: t('enterprise_ui_advanced_language_title'), description: t('enterprise_ui_advanced_language_description') },
  { key: 'glossary', id: 9999, title: t('glossary'), description: t('enterprise_ui_advanced_glossary_description') },
])

const createDraft = (): EnterpriseAdvancedSettingsValue => ({
  activeSections: [],
  optionsActive: {},
  promptWordIds: '',
  customizedStyle: '',
  termIds: '',
})

const draft = reactive<EnterpriseAdvancedSettingsValue>(createDraft())
const activeSections = computed(() => draft.activeSections)

const optionGroups = computed<Record<Exclude<SectionKey, 'glossary'>, PromptOption[]>>(() => {
  const findChildren = (id: number) => promptConfig.value.find(item => Number(item.id) === id)?.children || []
  return {
    ai: findChildren(1),
    style: findChildren(2),
    industry: findChildren(10),
    language: findChildren(11),
  }
})

function getOptionLabel(option: PromptOption) {
  if (option.wordsKey && te(option.wordsKey)) {
    return t(option.wordsKey)
  }
  return option.promptWord || option.wordsKey || String(option.id)
}

function isSectionActive(key: SectionKey) {
  return draft.activeSections.includes(key)
}

function isSectionDisabled(key: SectionKey) {
  return key === 'language' && props.disableKeepLanguage
}

function isOptionActive(key: Exclude<SectionKey, 'glossary'>, id: number) {
  return draft.optionsActive[key]?.includes(id) || false
}

function toggleSection(item: { key: SectionKey; id: number }) {
  if (isSectionDisabled(item.key)) return
  if (isSectionActive(item.key)) {
    draft.activeSections = draft.activeSections.filter(key => key !== item.key)
    draft.optionsActive[item.key] = []
    if (item.key === 'style') {
      draft.customizedStyle = ''
      customStyleActive.value = false
    }
    if (item.key === 'industry') {
      otherIndustryActive.value = false
      customIndustry.value = ''
    }
    if (item.key === 'glossary') {
      draft.termIds = ''
      termPickerActiveIds.value = ''
      termPickerKey.value += 1
    }
    return
  }

  draft.activeSections = [...draft.activeSections, item.key]
  if (item.key !== 'glossary') {
    const firstOption = optionGroups.value[item.key][0]
    draft.optionsActive[item.key] = firstOption ? [firstOption.id] : []
  }
}

function selectSingleOption(key: Exclude<SectionKey, 'glossary'>, id: number) {
  const selected = draft.optionsActive[key] || []
  draft.optionsActive[key] = key === 'language' && selected.includes(id) ? [] : [id]
  if (key === 'industry') {
    otherIndustryActive.value = false
    customIndustry.value = ''
  }
}

function selectStyleOption(id: number) {
  customStyleActive.value = false
  draft.customizedStyle = ''
  selectSingleOption('style', id)
}

function selectCustomStyle() {
  customStyleActive.value = true
  draft.optionsActive.style = []
}

function selectOtherIndustry() {
  otherIndustryActive.value = !otherIndustryActive.value
  draft.optionsActive.industry = []
  if (!otherIndustryActive.value) {
    customIndustry.value = ''
  }
}

async function submitCustomIndustry() {
  const promptWord = customIndustry.value.trim()
  if (!promptWord) {
    Toast(t('enterprise_ui_advanced_industry_required'))
    return
  }

  loading.value = true
  try {
    const response: any = await HttpRequest.post(PromptApi.addUserSuggestionPrompt, {
      parentId: 10,
      promptWord,
    })
    if (String(response?.code) === '1') {
      Toast(t('enterprise_ui_advanced_industry_feedback', { industry: promptWord }))
      customIndustry.value = ''
      otherIndustryActive.value = false
    } else {
      Toast(response?.message || t('enterprise_ui_advanced_submission_failed'))
    }
  } catch (error: any) {
    Toast(error?.message || t('enterprise_ui_advanced_submission_failed'))
  } finally {
    loading.value = false
  }
}

function updateTerms(terms: Array<{ id?: number | string }>) {
  draft.termIds = terms
    .map(item => item.id)
    .filter(id => id !== undefined && id !== null && id !== '')
    .join(',')
}

function syncDraft(value: Partial<EnterpriseAdvancedSettingsValue> = {}) {
  const next = createDraft()
  next.activeSections = [...(value.activeSections || [])]
  next.optionsActive = Object.fromEntries(
    Object.entries(value.optionsActive || {}).map(([key, ids]) => [key, [...(ids || [])]])
  )
  next.promptWordIds = value.promptWordIds || ''
  next.customizedStyle = value.customizedStyle || ''
  next.termIds = value.termIds || ''
  Object.assign(draft, next)
  customStyleActive.value = Boolean(next.customizedStyle)
  if (customStyleActive.value) {
    draft.optionsActive.style = []
  }
  otherIndustryActive.value = false
  customIndustry.value = ''
  termPickerActiveIds.value = next.termIds
  termPickerKey.value += 1
}

function parsePromptWordIds(value: unknown) {
  const source = Array.isArray(value) ? value : String(value || '').split(',')
  return source
    .map(item => Number(String(item).trim()))
    .filter(id => Number.isFinite(id))
}

function buildSettingsFromLastConfig(value: {
  promptWordIds?: unknown
  customizedStyle?: string
  termIds?: string
} = {}): EnterpriseAdvancedSettingsValue {
  const settings = createDraft()
  const customStyle = value.customizedStyle?.trim() || ''
  const optionMap = new Map<number, { key: Exclude<SectionKey, 'glossary'>; parentId: number }>()

  ;(['ai', 'style', 'industry', 'language'] as const).forEach((key) => {
    const parentId = sections.value.find(section => section.key === key)?.id
    if (!parentId) return
    optionGroups.value[key].forEach(option => optionMap.set(Number(option.id), { key, parentId }))
  })

  parsePromptWordIds(value.promptWordIds).forEach((id) => {
    const option = optionMap.get(id)
    if (!option || (option.key === 'language' && props.disableKeepLanguage)) return
    if (option.key === 'style' && customStyle) return
    const selected = settings.optionsActive[option.key] || []
    if (option.key === 'language') {
      if (!selected.length) settings.optionsActive[option.key] = [id]
    } else if (!selected.includes(id)) {
      settings.optionsActive[option.key] = [...selected, id]
    }
  })

  settings.customizedStyle = customStyle
  settings.termIds = value.termIds || ''
  const active = new Set<SectionKey>()
  Object.entries(settings.optionsActive).forEach(([key, ids]) => {
    if (ids?.length) active.add(key as SectionKey)
  })
  if (customStyle) active.add('style')
  if (settings.termIds) active.add('glossary')
  settings.activeSections = sections.value.map(section => section.key).filter(key => active.has(key))
  settings.promptWordIds = Object.values(settings.optionsActive).flatMap(ids => ids || []).join(',')
  return settings
}

async function syncFromLastConfig(value: {
  promptWordIds?: unknown
  customizedStyle?: string
  termIds?: string
} = {}) {
  await loadPromptConfig()
  const settings = buildSettingsFromLastConfig(value)
  syncDraft(settings)
  return settings
}

async function open() {
  await loadPromptConfig()
  syncDraft(props.modelValue)
  visible.value = true
}

function close() {
  visible.value = false
}

function confirm() {
  const promptWordIds = Object.values(draft.optionsActive)
    .flatMap(ids => ids || [])
    .filter(id => Number.isFinite(Number(id)))
    .join(',')
  const value: EnterpriseAdvancedSettingsValue = {
    activeSections: [...draft.activeSections],
    optionsActive: Object.fromEntries(
      Object.entries(draft.optionsActive).map(([key, ids]) => [key, [...(ids || [])]])
    ),
    promptWordIds,
    customizedStyle: draft.customizedStyle.trim(),
    termIds: draft.termIds,
  }
  emit('confirm', value)
  close()
}

async function loadPromptConfig() {
  if (promptConfig.value.length || loading.value) return
  loading.value = true
  try {
    const response: any = await HttpRequest.get(PromptApi.getCustomPromptConfig, null)
    if (String(response?.code) === '1') {
      promptConfig.value = Array.isArray(response.list) ? response.list : []
    }
  } catch (error) {
    console.error('load enterprise prompt config failed', error)
  } finally {
    loading.value = false
  }
}

watch(visible, (isVisible) => {
  if (isVisible) void loadPromptConfig()
})

watch(() => props.disableKeepLanguage, (disabled) => {
  if (!disabled) return
  draft.optionsActive.language = []
  draft.activeSections = draft.activeSections.filter(key => key !== 'language')
})

defineExpose({ open, syncFromLastConfig })
</script>

<style lang="scss">
.enterprise-advanced-settings-overlay {
  background: rgba(15, 23, 42, 0.24);

  .el-overlay-dialog {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
  }
}

.enterprise-advanced-settings-dialog {
  max-width: calc(100vw - 32px);
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden;
  border-radius: 16px !important;
  box-shadow: 0 16px 44px rgba(10, 35, 77, 0.2) !important;

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
  }
}

.enterprise-advanced-settings {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 24px 26px;
  color: #1f2329;
  background: #fff;

  *, *::before, *::after {
    box-sizing: border-box;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    h2 {
      margin: 0;
      font-size: 17px;
      font-weight: 800;
      line-height: 24px;
    }

    button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      padding: 0;
      color: #98a2b3;
      background: transparent;
      border: 0;
      border-radius: 7px;
      cursor: pointer;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
  }

  &__card {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-height: 72px;
    padding: 14px 16px;
    text-align: left;
    background: #fff;
    border: 1px solid #e3e7ec;
    border-radius: 10px;
    cursor: pointer;
    transition: border-color .18s ease, background-color .18s ease;

    &:hover,
    &.is-active {
      background: #f4f8ff;
      border-color: #2f80ed;
    }

    &.is-disabled {
      color: #98a2b3;
      background: #f7f9fc;
      cursor: not-allowed;
      opacity: .58;
    }
  }

  &__card-title {
    color: #1f2329;
    font-size: 13.5px;
    font-weight: 700;
  }

  &__card-desc {
    color: #98a2b3;
    font-size: 11.5px;
    line-height: 1.55;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 14px;
    max-height: 38vh;
    overflow-y: auto;

    section {
      padding-top: 14px;
      border-top: 1px solid #eef1f5;
    }

    h3 {
      margin: 0 0 10px;
      color: #49515d;
      font-size: 12.5px;
      font-weight: 700;
    }

    p {
      margin: 8px 0 0;
      color: #98a2b3;
      font-size: 11.5px;
      line-height: 1.55;
    }
  }

  &__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    button {
      min-height: 32px;
      padding: 0 14px;
      color: #5f6672;
      background: #fff;
      border: 1px solid #d9dee5;
      border-radius: 16px;
      cursor: pointer;

      &:hover,
      &.is-active {
        color: #2f80ed;
        background: #eaf3ff;
        border-color: #2f80ed;
      }
    }
  }

  &__textarea {
    margin-top: 10px;
  }

  &__counter {
    text-align: right;
  }

  &__inline-form {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px;
    margin-top: 10px;

    > button {
      min-width: 76px;
      color: #2f80ed;
      background: #fff;
      border: 1px solid #2f80ed;
      border-radius: 8px;
      cursor: pointer;
    }
  }

  &__term-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
    min-width: 0;

    a {
      color: #2f80ed;
      font-size: 12.5px;
      text-decoration: none;
    }
  }

  &__term-select {
    display: flex;
    align-items: center;
    width: 100%;
    min-width: 0;
    min-height: 40px;
    padding: 0 12px;
    background: #fff;
    border: 1px solid #d9dee5;
    border-radius: 10px;
    transition: border-color .18s ease, box-shadow .18s ease;

    &:hover,
    &:focus-within {
      border-color: #2f80ed;
      box-shadow: 0 0 0 2px rgba(47, 128, 237, .08);
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding-top: 2px;

    button {
      height: 38px;
      padding: 0 22px;
      font-size: 13px;
      font-weight: 600;
      border-radius: 19px;
      cursor: pointer;
    }

    .is-secondary {
      color: #5f6672;
      background: #fff;
      border: 1px solid #d9dee5;
    }

    .is-primary {
      color: #fff;
      background: #2f80ed;
      border: 1px solid #2f80ed;
    }
  }
}

@media (max-width: 720px) {
  .enterprise-advanced-settings {
    padding: 20px;

    &__grid {
      grid-template-columns: 1fr;
    }

    &__placeholder {
      display: none;
    }
  }
}
</style>
